import { parseCaption } from "@/utils/captionUtils";
import cn from "classnames";
import React, { useLayoutEffect, useRef, useState } from "react";

interface CaptionExpandProps {
  /** The full caption text to display */
  text: string;
  /** The number of lines to display initially */
  initialLines: number;
  /** Text for the button to expand the caption (e.g., "...more") */
  showMoreText: string;
  /** Optional text for the button to collapse the caption (e.g., "Show less") */
  showLessText?: string;
  /** Color theme for the expand/collapse button */
  linkColor: "blue" | "gray" | "white";
  /** Optional additional CSS classes for the container */
  className?: string;
  /** Indicates if the text direction is Right-to-Left */
  isRTL?: boolean;
}

const CaptionExpand: React.FC<CaptionExpandProps> = ({
  text,
  initialLines,
  showMoreText,
  showLessText,
  linkColor,
  className,
  isRTL = false, // Default to LTR
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(!!text);
  const contentRef = useRef<HTMLParagraphElement>(null);

  // Using useLayoutEffect to measure right after render but before paint
  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      const element = contentRef.current;
      if (element) {
        // Temporarily remove line clamp to measure full height
        const originalLineClamp = element.style.webkitLineClamp;
        element.style.webkitLineClamp = "unset";

        const computedStyle = window.getComputedStyle(element);
        const lineHeight = parseFloat(computedStyle.lineHeight);
        // Add a small buffer (e.g., 1) in case of rounding issues
        const maxHeight = lineHeight * initialLines + 1;

        // Check if content height exceeds max height for initial lines
        if (element.scrollHeight > maxHeight) {
          setNeedsTruncation(true);
        } else {
          setNeedsTruncation(false);
          setIsExpanded(false); // Reset expanded state if it fits
        }

        // Restore original line clamp if it was set
        element.style.webkitLineClamp = originalLineClamp || "";
      }
    }, 0); // Delay measurement slightly

    // Cleanup function to clear the timeout
    return () => clearTimeout(timeoutId);
  }, [text, initialLines]); // Re-run if text or initialLines change

  const toggleExpand = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation(); // Stop the event from bubbling up
    setIsExpanded(!isExpanded);
  };

  const linkClasses = cn("cursor-pointer font-medium", {
    "text-blue-600 hover:text-blue-700": linkColor === "blue",
    "text-gray-500 hover:text-gray-600": linkColor === "gray",
    "text-white hover:text-gray-200": linkColor === "white",
  });

  // Determine necessary padding based on direction and state
  const paddingStyle: React.CSSProperties = {};
  if (!isExpanded && needsTruncation) {
    // Add padding to the end side (right for LTR, left for RTL)
    // Adjust the value (e.g., '5em') based on button width and desired spacing
    paddingStyle[isRTL ? "paddingLeft" : "paddingRight"] = "5em";
  }

  return (
    <div className={cn("relative", className)}>
      <p
        ref={contentRef}
        className={cn("whitespace-pre-line break-words", {
          "overflow-hidden": !isExpanded && needsTruncation,
          "[display:-webkit-box] [-webkit-box-orient:vertical]":
            !isExpanded && needsTruncation, // Apply line clamp styles only when needed
          "text-right": isRTL, // Apply text-right for RTL
          "text-left": !isRTL, // Explicitly set text-left for LTR
        })}
        style={
          {
            WebkitLineClamp:
              !isExpanded && needsTruncation ? initialLines : "unset",
            ...paddingStyle, // Apply dynamic padding END
          } as React.CSSProperties
        }
        dir={isRTL ? "rtl" : "ltr"} // Add dir attribute for accessibility/rendering
      >
        {parseCaption(text)}
      </p>
      {needsTruncation && !isExpanded && (
        <button
          onClick={toggleExpand}
          className={cn(
            "absolute bottom-0 cursor-pointer font-medium", // Use absolute positioning
            linkClasses, // Apply color styles
            {
              "right-0 pl-1": !isRTL, // Position right and add slight left padding for LTR
              "left-0 pr-1": isRTL, // Position left and add slight right padding for RTL
            }
          )}
        >
          {showMoreText}
        </button>
      )}
      {needsTruncation && isExpanded && showLessText && (
        <button
          onClick={toggleExpand}
          className={cn(linkClasses, "mt-1 inline-block")}
        >
          {showLessText}
        </button>
      )}
    </div>
  );
};

export default CaptionExpand;
